import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import SessionTimeoutModal from "../components/SessionTimeoutModal/SessionTimeoutModal";

// ── Change SESSION_MS to adjust the inactivity timeout everywhere ─────────────
const SESSION_MS = 30 * 60 * 1000;  // 30 min
const IDLE_MS    = SESSION_MS;
const WARNING_MS = 60 * 1000;
// A login lasts at most JWT_MAX_SESSION (backend, default 12 h) even with activity;
// the user is warned this long before that hard end.
const END_WARNING_MS = 5 * 60 * 1000;

// Last activity is kept in localStorage so every open tab shares one idle clock.
const ACTIVITY_KEY = "_lastActivity";
// session_end the user already dismissed the end warning for (shared by all tabs)
const END_ACK_KEY = "_sessionEndAck";

const readLastActivity = () => {
  const v = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
  return Number.isFinite(v) ? v : 0;
};

const decodeToken = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return null;
  }
};

const isJwtExpired = () => {
  try {
    const stored  = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    const decoded = decodeToken(stored.jwt);
    return !decoded?.exp || decoded.exp < Math.floor(Date.now() / 1000);
  } catch (_) {
    return true;
  }
};

// Hard end of this login in ms (session_end claim), Infinity for tokens without it
const readSessionEnd = () => {
  try {
    const stored  = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    const decoded = decodeToken(stored.jwt);
    return decoded?.session_end ? decoded.session_end * 1000 : Infinity;
  } catch (_) {
    return Infinity;
  }
};

const AuthContext = createContext();

// ── Timeout controller rendered as a sibling to {children} inside AuthProvider.
// Its own state changes (deadline) never touch the app tree.
const SessionTimeoutController = () => {
  const { isAuthenticated, logoutUser } = useAuth();
  // null = no warning; otherwise { type: "idle" | "end", deadline } — deadline is the
  // shared logout time (ms) the countdown runs to
  const [warning, setWarning] = useState(null);

  const timerRef        = useRef(null);
  const scheduleRef     = useRef(() => {});
  const pingDebounceRef = useRef(null);

  const performLogout = useCallback(() => {
    clearTimeout(timerRef.current);
    setWarning(null);
    logoutUser();
  }, [logoutUser]);

  // Single source of truth: derive this tab's state from the shared last-activity time.
  // Called on every activity (in any tab), on wake/focus, and when a timer fires —
  // a timer firing late or in a background tab simply re-reads the shared time.
  const schedule = useCallback(() => {
    clearTimeout(timerRef.current);
    const now       = Date.now();
    const idleEnd   = readLastActivity() + IDLE_MS;
    const warnAt    = idleEnd - WARNING_MS;
    const hardEnd   = readSessionEnd();
    const endWarnAt = hardEnd - END_WARNING_MS;
    const logoutAt  = Math.min(idleEnd, hardEnd);

    if (isJwtExpired() || now >= logoutAt) {
      performLogout();
      return;
    }
    if (now >= warnAt && idleEnd <= hardEnd) {
      setWarning({ type: "idle", deadline: idleEnd });
    } else if (now >= endWarnAt) {
      const acked = localStorage.getItem(END_ACK_KEY) === String(hardEnd);
      setWarning(acked ? null : { type: "end", deadline: hardEnd });
    } else {
      setWarning(null);
    }
    // Wake up at the next moment something changes
    const next = Math.min(...[warnAt, endWarnAt, logoutAt].filter((t) => t > now));
    timerRef.current = setTimeout(() => scheduleRef.current(), next - now);
  }, [performLogout]);

  useEffect(() => { scheduleRef.current = schedule; }, [schedule]);

  const recordActivity = useCallback(() => {
    if (!localStorage.getItem("AUTH_DATA")) return;
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    schedule();
  }, [schedule]);

  // "Stay" — close the popup in every tab, restart the idle clock, and refresh the JWT via ping.
  const handleStay = useCallback(async () => {
    if (isJwtExpired()) {
      performLogout();
      return;
    }
    recordActivity();
    try {
      const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ping`, {
        headers: {
          Authorization: `Bearer ${stored.jwt}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      // 401 means the server confirms the token is invalid → logout
      if (res.status === 401) performLogout();
      // any other failure (network, 5xx) → stay logged in, idle clock already reset
    } catch {
      // network error — keep the session alive, next real API call will confirm
    }
  }, [recordActivity, performLogout]);

  // "Verstanden" on the end warning — hide it in every tab; the logout time stays the same.
  const handleEndAck = useCallback(() => {
    localStorage.setItem(END_ACK_KEY, String(readSessionEnd()));
    schedule();
  }, [schedule]);

  // Debounced ping — fires at most once every 60 s on button clicks to keep JWT alive.
  const debouncedPing = useCallback(() => {
    if (pingDebounceRef.current) return;
    pingDebounceRef.current = setTimeout(() => {
      pingDebounceRef.current = null;
    }, 60 * 1000);
    if (isJwtExpired()) return;
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    if (!stored.jwt) return;
    fetch(`${process.env.REACT_APP_API_URL}/api/ping`, {
      headers: {
        Authorization: `Bearer ${stored.jwt}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimeout(timerRef.current);
      setWarning(null);
      return;
    }

    // Fresh login → start the shared clock; reload or extra tab → continue the existing one.
    if (!readLastActivity()) recordActivity();
    else schedule();

    // Any button click counts as activity and triggers a debounced JWT refresh.
    const onClick = (e) => {
      if (e.target.closest("button")) {
        recordActivity();
        debouncedPing();
      }
    };
    // Any successful API call (jwt-refreshed) counts as activity.
    const onRefreshed = () => recordActivity();
    // Activity or a dismissed end warning in another tab → re-evaluate here (closes this tab's popup).
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY || e.key === END_ACK_KEY) schedule();
    };
    // Wake from sleep / tab shown again: timers may have been paused, so re-check immediately.
    const onVisible = () => {
      if (document.visibilityState === "visible") schedule();
    };

    document.addEventListener("click", onClick);
    window.addEventListener("jwt-refreshed", onRefreshed);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", schedule);
    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener("click", onClick);
      window.removeEventListener("jwt-refreshed", onRefreshed);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", schedule);
    };
  }, [isAuthenticated, schedule, recordActivity, debouncedPing]);

  if (warning === null || !isAuthenticated) return null;

  if (warning.type === "end") {
    return (
      <SessionTimeoutModal
        variant="end"
        deadline={warning.deadline}
        warningDurationSecs={Math.round(END_WARNING_MS / 1000)}
        onStay={handleEndAck}
      />
    );
  }

  return (
    <SessionTimeoutModal
      deadline={warning.deadline}
      warningDurationSecs={Math.round(WARNING_MS / 1000)}
      onStay={handleStay}
      onLogout={performLogout}
    />
  );
};

// ── AuthProvider: only auth state, no timeout state ───────────────────────────

export const AuthProvider = ({ children }) => {
  const [user, setUser]                     = useState(null);
  const [loginError, setLoginError]         = useState(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const isAuthenticated = !!user;

  // Lets the fetch interceptor know whether this tab is logged in
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── Logout (declared first so the fetch interceptor can reference it) ────────

  const logoutUser = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setLoginError(null);
  }, []);

  // ── Keep all tabs in sync: logout in one tab logs out every tab ──────────────

  useEffect(() => {
    const onStorage = (e) => {
      // key === null → localStorage.clear() in another tab
      if (e.key !== null && e.key !== "AUTH_DATA") return;
      const raw = localStorage.getItem("AUTH_DATA");
      if (!raw) {
        setUser(null);
        return;
      }
      // Logged in from another tab → pick up the session here too
      try {
        const parsed  = JSON.parse(raw);
        const decoded = decodeToken(parsed.jwt);
        setUser((prev) => prev ?? (decoded ? { ...parsed, decoded } : parsed));
      } catch (_) {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ── Rolling token + security checks on every fetch ───────────────────────────

  useEffect(() => {
    const original = window.fetch.bind(window);
    window.fetch = async (...args) => {
      // Pre-request: if JWT exists but is already expired, logout immediately.
      try {
        const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
        if (stored.jwt && isJwtExpired()) {
          logoutUser();
          return new Response(
            JSON.stringify({ success: false, message: "Session expired" }),
            { status: 401, headers: { "Content-Type": "application/json" } }
          );
        }
      } catch (_) {}

      const response = await original(...args);

      // Post-response: if server rejects the JWT, force logout — also when the token
      // is already gone from storage but this tab still believes it is logged in.
      if (response.status === 401) {
        try {
          const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
          if (stored.jwt || userRef.current) logoutUser();
        } catch (_) {}
      }

      const fresh = response.headers.get("X-Refreshed-Token");
      if (fresh) {
        try {
          const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
          if (stored.jwt) {
            stored.jwt = fresh;
            localStorage.setItem("AUTH_DATA", JSON.stringify(stored));
          }
        } catch (_) {}
        window.dispatchEvent(new Event("jwt-refreshed"));
      }

      return response;
    };
    return () => { window.fetch = original; };
  }, [logoutUser]);

  // ── Restore session from localStorage on mount ──────────────────────────────

  useEffect(() => {
    const stored = localStorage.getItem("AUTH_DATA");
    if (stored) {
      try {
        const parsed  = JSON.parse(stored);
        const decoded = decodeToken(parsed.jwt);
        if (decoded?.exp && decoded.exp < Math.floor(Date.now() / 1000)) {
          localStorage.clear();
          return;
        }
        setUser(decoded ? { ...parsed, decoded } : parsed);
      } catch (err) {
        localStorage.removeItem("AUTH_DATA");
      }
    }
  }, []);

  // ── Login ───────────────────────────────────────────────────────────────────

  const loginUser = useCallback(async (email, password) => {
    setIsLoginLoading(true);
    setLoginError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
      });

      let data;
      try { data = await res.json(); } catch (_) { data = null; }

      if (!res.ok || !data?.jwt) {
        const message =
          res.status >= 500
            ? "Anmeldung fehlgeschlagen."
            : data?.message || "Anmeldung fehlgeschlagen.";
        setLoginError(message);
        return { success: false, error: message };
      }

      const userData = {
        jwt:               data.jwt,
        token:             data.token,
        customerId:        data.customerId,
        customerLastName:  data.customerLastName,
        customerFirstName: data.customerFirstName,
        customerCompany:   data.customerCompany,
        employerId:        data.employerId,
        testUser:          data.testUser === true, // TEMPORÄRER TESTZUGANG
      };

      const decoded  = decodeToken(data.jwt);
      const fullUser = decoded ? { ...userData, decoded } : userData;

      localStorage.removeItem(ACTIVITY_KEY);
      localStorage.setItem("AUTH_DATA", JSON.stringify(userData));
      setUser(fullUser);
      return { success: true, error: null };
    } catch (err) {
      const message = "Anmeldung fehlgeschlagen.";
      setLoginError(message);
      return { success: false, error: message };
    } finally {
      setIsLoginLoading(false);
    }
  }, []);

  // ── Stable context value ────────────────────────────────────────────────────

  const contextValue = useMemo(() => ({
    user,
    isAuthenticated,
    loginUser,
    logoutUser,
    loginError,
    setLoginError,
    isLoginLoading,
  }), [user, isAuthenticated, loginUser, logoutUser, loginError, isLoginLoading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <SessionTimeoutController />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
